import axios from 'axios';
import {api} from './constants/api';

let _accessToken = null;
let _isRefreshing = false;
let _refreshSubscribers = [];

function onTokenRefreshed(newToken) {
    _refreshSubscribers.forEach(callback => callback(newToken));
    _refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
    _refreshSubscribers.push(callback);
}

const request = (url, options = {}) => {
    return fetch(url, options)
        .then(response => {
            if (response.status !== 200) {
                return response.json().then(error => {
                    throw error;
                });
            }
            return response.json();
        })
        .then(json => {
            return json;
        })
        .catch(error => {
            return {error: true, detail: error.detail}
        })
};

class ApiClient {
    constructor() {
        this.api = api;
        this.instance = axios.create({
            baseURL: api.config.url.API_URL,
        });
        this._setupInterceptors();
    }

    setToken = (token) => {
        _accessToken = token;
        if (token) {
            this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
            delete this.instance.defaults.headers.common.Authorization;
        }
    };

    getToken = () => {
        return _accessToken;
    };

    clearToken = () => {
        _accessToken = null;
        delete this.instance.defaults.headers.common.Authorization;
    };

    // Kept for backward compatibility — calls setToken internally
    updateToken = () => {
        if (_accessToken) {
            this.instance.defaults.headers.common.Authorization = `Bearer ${_accessToken}`;
        }
    };

    refreshAccessToken = async () => {
        try {
            const response = await fetch(api.refresh(), {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                this.setToken(data.access_token);
                return data.access_token;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    _setupInterceptors = () => {
        this.instance.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;

                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    if (_isRefreshing) {
                        return new Promise(resolve => {
                            addRefreshSubscriber(newToken => {
                                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                                resolve(this.instance(originalRequest));
                            });
                        });
                    }

                    originalRequest._retry = true;
                    _isRefreshing = true;

                    try {
                        const newToken = await this.refreshAccessToken();
                        if (newToken) {
                            onTokenRefreshed(newToken);
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return this.instance(originalRequest);
                        } else {
                            _refreshSubscribers = [];
                            window.location.href = '/account/login';
                            return Promise.reject(error);
                        }
                    } catch (refreshError) {
                        _refreshSubscribers = [];
                        window.location.href = '/account/login';
                        return Promise.reject(refreshError);
                    } finally {
                        _isRefreshing = false;
                    }
                }
                return Promise.reject(error);
            }
        );
    };

    request = (url, options = {}) => {
        return this.instance[options.method](url, options.body)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    head = (url, options = {}) => {
        return this.instance
            .head(url, options)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    download = (url, params?) => {
        const searchTerm = params ? params['search_term'] : {};
        return this.instance
            .post(url, searchTerm)
            .then(response => {
                const link = document.createElement('a');
                link.href = response.data.data.result;
                const fileName = response.headers['file-name']
                link.setAttribute('download', fileName ? fileName : '');
                document.body.appendChild(link);
                link.click();

                return response
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    export = (url, params?) => {
        const searchTerm = params ? params['search_term'] : {};
        return this.instance
            .post(url, searchTerm)
            .then(response => {
                return response
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    post = (url, body, headers) => {
        return this.instance
            .post(url, body, headers)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    get = (resourceName, resourceId) => {
        return this.instance
            .get(`${resourceName}/${resourceId}`)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    list = (
        resourceName,
        page = undefined, sizePerPage = undefined, searchText = undefined, sort = undefined, filters = undefined,
        customParams = undefined
    ) => {
        let params = {params: {}}
        if (typeof page !== 'undefined' & typeof sizePerPage !== 'undefined') {
            params.params['page'] = page
            params.params['sizePerPage'] = sizePerPage
        }
        if (typeof searchText !== 'undefined' && searchText !== '') {
            params.params['searchText'] = searchText
        }
        if (typeof sort !== 'undefined' && sort !== '') {
            params.params['sort'] = sort
        }
        if (typeof filters !== 'undefined' && filters !== '') {
            params.params['filters'] = filters
        }
        if (!resourceName.endsWith('/') && resourceName.split('/').length < 2) {
            resourceName += '/';
        }

        if (customParams) {
            for (const key in customParams) {
                params.params[key] = customParams[key]
            }
        }

        if (Object.keys(params.params).length === 0) {
            return this.instance
                .get(resourceName)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    return {error: true, detail: error};
                });
        } else {
            return this.instance
                .get(resourceName, params)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    return {error: true, detail: error};
                });
        }
    };

    create = (resourceName, body) => {
        if (!resourceName.endsWith('/') && resourceName.split('/').length < 2) {
            resourceName += '/';
        }

        return this.instance
            .post(resourceName, body)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error.response.data.detail};
            })
    };

    update = (resourceName, resourceId, body) => {
        return this.instance
            .put(`${resourceName}/${resourceId}`, body)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    updateBatch = (resourceName, body) => {
        return this.instance
            .put(`${resourceName}`, body)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    show = (resourceName, resourceId) => {
        return this.instance
            .get(`${resourceName}/${resourceId}`)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    deleteBatch = (resourceName, resource_ids, options?) => {
        return this.instance
            .post(resourceName + '/delete-requests/id', {resource_ids, options})
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };

    uploadFileIntoS3 = (file, url, key_fields) => {
        let formData = new FormData();
        for (const p in key_fields) {
            formData.set(p, key_fields[p]);
        }
        formData.append('file', file);

        return axios.post(url, formData)
    }

    edit = ({endpoint, id, body, query = ''}) => {
        return this.instance
            .put(`${endpoint}/${id}${query}`, body)
            .then(response => {
                return response;
            })
            .catch(error => {
                return {error: true, detail: error};
            })
    };
}

export const apiClient = new ApiClient();

export {request};
